import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Leaf, ArrowRight, GraduationCap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useI18n } from "./i18n";
import LangToggle from "./components/LangToggle";
import StandardsLogos from "./components/StandardsLogos";
import ConsentCheckboxes from "./components/ConsentCheckboxes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 75,
    tagline: "Küçük işletme · Small business",
    taglineEn: "Small business · Solo operator",
    bestFor_tr: "Örnek: İhracatçı KOBİ, tek tesisli üretici, mikro işletme",
    bestFor_en: "Example: Exporting SMB, single-site producer, micro business",
    features_tr: [
      "3 tesise kadar",
      "1 kullanıcı",
      "Kapsam 1-2-3 emisyon hesaplama",
      "PDF karbon raporu",
      "CBAM veri girişi",
      "E-mail destek",
    ],
    features_en: [
      "Up to 3 facilities",
      "1 user",
      "Scope 1-2-3 emissions",
      "PDF carbon report",
      "CBAM data entry",
      "E-mail support",
    ],
    accent: "#8ea67a",
  },
  {
    id: "growth",
    name: "Growth",
    price: 149,
    highlight: true,
    tagline: "KOBİ+ · SME",
    taglineEn: "SME · Growing team",
    bestFor_tr: "Örnek: CBAM'e tabi çelik/çimento üreticisi, çok tesisli KOBİ+",
    bestFor_en: "Example: CBAM-liable steel/cement producer, multi-site SME",
    features_tr: [
      "10 tesise kadar",
      "3 kullanıcı",
      "White-label markalama",
      "Public paylaşım linkleri",
      "CBAM PDF + XML export",
      "IFRS S1 / S2, GRI, ESRS raporları",
      "Öncelikli destek",
    ],
    features_en: [
      "Up to 10 facilities",
      "3 users",
      "White-label branding",
      "Public share links",
      "CBAM PDF + XML export",
      "IFRS S1 / S2, GRI, ESRS reports",
      "Priority support",
    ],
    accent: "#14512f",
  },
  {
    id: "consultant",
    name: "Consultant",
    price: 249,
    tagline: "Danışmanlar · B2B",
    taglineEn: "Consultants · B2B",
    bestFor_tr: "Örnek: Sürdürülebilirlik danışmanlığı, 5-30 müşterili portföy",
    bestFor_en: "Example: Sustainability consultancy, 5-30 client portfolio",
    features_tr: [
      "Sınırsız müşteri iş alanı",
      "Sınırsız kullanıcı",
      "Her müşteri için ayrı beyaz etiket",
      "Multi-tenant izolasyon",
      "Denetim izi (audit log)",
      "Öncelikli destek + Slack kanalı",
    ],
    features_en: [
      "Unlimited client workspaces",
      "Unlimited users",
      "Per-client white-label",
      "Multi-tenant isolation",
      "Audit log",
      "Priority support + Slack channel",
    ],
    accent: "#2f8f5b",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 499,
    priceSuffix: "+",
    tagline: "Kurumsal",
    taglineEn: "Enterprise",
    bestFor_tr: "Örnek: Holding, çok iştirakli grup, listelenen şirket",
    bestFor_en: "Example: Holding, multi-entity group, listed company",
    features_tr: [
      "SSO / SAML",
      "Özel entegrasyon (ERP, IoT)",
      "Dedicated altyapı",
      "SLA + adanmış destek",
      "Özel eğitim ve on-boarding",
      "Sözleşme + fatura özelleştirme",
    ],
    features_en: [
      "SSO / SAML",
      "Custom integration (ERP, IoT)",
      "Dedicated infrastructure",
      "SLA + dedicated support",
      "Custom training & onboarding",
      "Contract & invoice customisation",
    ],
    accent: "#0f3d24",
  },
];

// Add-on: expert coaching by Barış Yüce, 35-yıllık çevre mühendisi.
const COACHING = {
  id: "coaching",
  price: 300,
  unit: "gün",
  unit_en: "day",
  bullets_tr: [
    "35+ yıl çevre mühendisi tecrübesi",
    "ISO 14064, GHG Protocol, CBAM, CSRD stratejisi",
    "Karbon envanterinizin kurumsal kurulumu",
    "Denetim öncesi hazırlık ve gözden geçirme",
    "Fiyat müzakereye açık (proje ve kapsam bazında)",
  ],
  bullets_en: [
    "35+ years of environmental engineering",
    "ISO 14064, GHG Protocol, CBAM, CSRD strategy",
    "Set up your carbon inventory end-to-end",
    "Pre-audit review and readiness",
    "Price is negotiable (per project & scope)",
  ],
};

function ContactModal({ open, onClose, plan }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", country: "", message: "" });
  const [consent, setConsent] = useState({ kvkk: false, etk: false });
  const [sending, setSending] = useState(false);
  const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const isConsultant = plan === "consultant";
  const canSubmit = form.name && form.email && consent.kvkk && !sending;

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(lang === "en" ? "Name and email are required" : "Ad ve e-posta zorunlu");
      return;
    }
    if (!consent.kvkk) {
      toast.error(lang === "en" ? "Please accept the KVKK notice to continue" : "Devam etmek için KVKK metnini onaylayın");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BACKEND}/api/leads`, {
        ...form,
        plan,
        kvkk_consent: consent.kvkk,
        etk_consent: consent.etk,
        sample_report: isConsultant,
      });
      toast.success(
        isConsultant
          ? (lang === "en"
              ? "Sample report sent — check your inbox in a few minutes"
              : "Örnek rapor gönderildi — birkaç dakika içinde gelen kutunuzu kontrol edin")
          : (lang === "en" ? "Thanks — we'll reply within 24 hours" : "Teşekkürler — 24 saat içinde döneceğiz")
      );
      onClose();
      setForm({ name: "", email: "", company: "", phone: "", country: "", message: "" });
      setConsent({ kvkk: false, etk: false });
    } catch (err) {
      toast.error(lang === "en" ? "Something went wrong, please try again" : "Bir sorun oluştu, tekrar deneyin");
    } finally {
      setSending(false);
    }
  };

  const planLabel = plan === "coaching"
    ? (lang === "en" ? "Coaching" : "Coaching")
    : PLANS.find((p) => p.id === plan)?.name || plan;

  const modalTitle = isConsultant
    ? (lang === "en" ? "Request a sample report" : "Örnek rapor iste")
    : planLabel;

  const submitLabel = isConsultant
    ? (lang === "en" ? "Send sample report" : "Örnek raporu gönder")
    : (lang === "en" ? "Send" : "Gönder");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="contact-modal" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[#8a8f83]">
            {isConsultant
              ? (lang === "en" ? "Consultants — one-time sample" : "Danışmanlar — tek seferlik örnek")
              : (lang === "en" ? "Selected plan" : "Seçilen plan")}
          </div>
          <div className="mt-1 font-display text-2xl text-[#14512f]" data-testid="modal-plan-label">{modalTitle}</div>
          {isConsultant && (
            <p className="mt-2 text-[12.5px] leading-relaxed text-[#5c6259]" data-testid="modal-sample-note">
              {lang === "en"
                ? "Fill in your details and we'll instantly email you a blank sample PDF report showing what Carbon Ledger produces."
                : "Bilgilerinizi doldurun; Carbon Ledger'ın ürettiği yapıyı gösteren boş bir örnek PDF raporu e-postanıza hemen gönderelim."}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input required data-testid="lead-name" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                 placeholder={lang === "en" ? "Full name" : "Ad Soyad"} value={form.name} onChange={up("name")} />
          <input required type="email" data-testid="lead-email" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                 placeholder={lang === "en" ? "Email" : "E-posta"} value={form.email} onChange={up("email")} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="lead-company" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                   placeholder={lang === "en" ? "Company" : "Şirket"} value={form.company} onChange={up("company")} />
            <input data-testid="lead-country" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                   placeholder={lang === "en" ? "Country" : "Ülke"} value={form.country} onChange={up("country")} />
          </div>
          <input data-testid="lead-phone" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                 placeholder={lang === "en" ? "Phone (optional)" : "Telefon (opsiyonel)"} value={form.phone} onChange={up("phone")} />
          <textarea data-testid="lead-message" rows="3" className="w-full rounded-lg border border-[#dcdfd4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#14512f]"
                    placeholder={lang === "en" ? "Message (optional)" : "Mesajınız (opsiyonel)"} value={form.message} onChange={up("message")} />

          <div className="rounded-lg border border-[#eef0e7] bg-[#f8faf3] px-3 py-3">
            <ConsentCheckboxes
              kvkk={consent.kvkk}
              etk={consent.etk}
              onChange={setConsent}
              testIdPrefix="lead"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-[#dcdfd4] bg-white px-4 py-2.5 text-sm font-medium text-[#4b544c] hover:bg-[#f5f6f1]" data-testid="lead-cancel">
              {lang === "en" ? "Cancel" : "İptal"}
            </button>
            <button type="submit" disabled={!canSubmit} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#14512f] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0f4526] disabled:cursor-not-allowed disabled:opacity-50" data-testid="lead-submit">
              {sending ? "..." : submitLabel} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t, lang } = useI18n();
  const [selected, setSelected] = useState(null);
  const [params] = useSearchParams();

  // Auto-open lead modal when navigated with ?plan=<id>
  useEffect(() => {
    const q = params.get("plan");
    if (!q) return;
    const validIds = ["starter", "growth", "consultant", "enterprise", "coaching"];
    if (validIds.includes(q)) setSelected(q);
  }, [params]);

  return (
    <div className="dash-scope min-h-screen bg-[#F5F6F1]">
      {/* Header */}
      <div className="border-b border-[#e4e6dd] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14512f] text-[#D4F870]"><Leaf className="h-5 w-5" /></span>
            <span className="font-display text-lg text-[#14512f]">Carbon Ledger</span>
          </Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link to="/login" className="text-sm font-medium text-[#14512f] hover:underline" data-testid="pricing-signin">
              {lang === "en" ? "Sign in" : "Giriş yap"}
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="mx-auto max-w-6xl px-6 pt-14 pb-8 text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[#dcdfd4] bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-[#4b544c]">
          <Sparkles className="h-3 w-3" /> {lang === "en" ? "3-day free trial" : "3 gün ücretsiz deneme"}
        </div>
        <h1 className="mt-4 font-display text-4xl leading-tight text-[#16321f] sm:text-5xl">
          {lang === "en" ? "Auditable carbon accounting" : "Denetlenebilir karbon hesaplaması"}
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-[15px] text-[#6b7268]">
          {lang === "en"
            ? "Scope 1-2-3, CBAM, IFRS S1/S2, ESRS and GRI in one auditable ledger."
            : "Kapsam 1-2-3, CBAM, IFRS S1/S2, ESRS ve GRI tek bir denetlenebilir defterde."}
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] text-[#6b7268]" data-testid="pricing-trust-row">
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#14512f]" strokeWidth={3} /> {lang === "en" ? "No credit card required" : "Kredi kartı gerekmez"}</span>
          <span className="hidden h-3 w-px bg-[#dcdfd4] sm:block" />
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#14512f]" strokeWidth={3} /> {lang === "en" ? "Response within 24 hours" : "24 saat içinde yanıt"}</span>
          <span className="hidden h-3 w-px bg-[#dcdfd4] sm:block" />
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[#14512f]" strokeWidth={3} /> {lang === "en" ? "Onboarding help included" : "Kurulum yardımı dahil"}</span>
        </div>
      </div>

      {/* Plans */}
      <div className="mx-auto max-w-6xl px-6 pb-12">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((p) => (
            <div key={p.id} data-testid={`plan-${p.id}`}
                 className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5 ${p.highlight ? "border-[#14512f] ring-2 ring-[#14512f]/20" : "border-[#e4e6dd]"}`}>
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#14512f] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                  {lang === "en" ? "Most popular" : "En popüler"}
                </div>
              )}
              <div className="text-xs font-medium uppercase tracking-widest" style={{ color: p.accent }}>
                {lang === "en" ? p.taglineEn : p.tagline}
              </div>
              <div className="mt-1 font-display text-2xl text-[#14512f]">{p.name}</div>
              {(lang === "en" ? p.bestFor_en : p.bestFor_tr) && (
                <div className="mt-1 text-[11px] leading-snug text-[#8a8f83]" data-testid={`bestfor-${p.id}`}>
                  {lang === "en" ? p.bestFor_en : p.bestFor_tr}
                </div>
              )}
              <div className="mt-3 flex items-baseline gap-2" data-testid={`price-${p.id}`}>
                <span className="font-display text-xl font-bold leading-tight text-[#14512f]">{lang === "en" ? "Tailored to Your Needs" : "İhtiyaçlarınıza Yönelik"}</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-[#4b544c]">
                {(lang === "en" ? p.features_en : p.features_tr).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-[3px] h-3.5 w-3.5 flex-shrink-0" style={{ color: p.accent }} strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => setSelected(p.id)} data-testid={`select-${p.id}`}
                      className={`mt-6 flex items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${p.highlight ? "bg-[#14512f] text-white hover:bg-[#0f4526]" : "border border-[#dcdfd4] bg-white text-[#14512f] hover:bg-[#eef6ef]"}`}>
                {lang === "en" ? "Get a quote" : "Teklif Alınız"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Coaching add-on */}
        <div className="mt-8 grid gap-4 rounded-2xl border border-[#e4e6dd] bg-white p-6 md:grid-cols-[auto_1fr_auto] md:items-center" data-testid="plan-coaching">
          <div className="hidden h-14 w-14 place-items-center rounded-xl bg-[#eef6ef] md:grid">
            <GraduationCap className="h-7 w-7 text-[#14512f]" />
          </div>
          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-display text-2xl text-[#14512f]">
                {lang === "en" ? "Coaching (expert-led)" : "Coaching (Uzman Danışmanlık)"}
              </span>
              <span className="text-sm text-[#8a8f83]">
                {lang === "en" ? "35+ yrs environmental engineering · Barış YÜCE" : "35+ yıl çevre mühendisliği · Barış YÜCE"}
              </span>
            </div>
            <ul className="mt-3 grid gap-1.5 text-sm text-[#4b544c] md:grid-cols-2">
              {(lang === "en" ? COACHING.bullets_en : COACHING.bullets_tr).map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="mt-[3px] h-3.5 w-3.5 flex-shrink-0 text-[#14512f]" strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <div className="text-right md:text-right" data-testid="price-coaching">
              <span className="font-display text-xl font-bold leading-tight text-[#14512f]">
                {lang === "en" ? "Tailored to Your Needs" : "İhtiyaçlarınıza Yönelik"}
              </span>
            </div>
            <button onClick={() => setSelected("coaching")} data-testid="select-coaching"
                    className="rounded-lg bg-[#14512f] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0f4526]">
              {lang === "en" ? "Book a call" : "Görüşme al"}
            </button>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-10 rounded-2xl border border-[#e4e6dd] bg-white p-6">
          <div className="mb-4 text-center text-[10px] font-medium uppercase tracking-[0.18em] text-[#8a8f83]">
            {t("standardsAligned")}
          </div>
          <div className="mx-auto max-w-lg">
            <StandardsLogos />
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-8 rounded-2xl border border-[#e4e6dd] bg-white p-6" data-testid="pricing-faq">
          <div className="mb-5 text-center">
            <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-[#8a8f83]">
              {lang === "en" ? "Frequently asked" : "Sık sorulanlar"}
            </div>
            <h3 className="mt-1 font-display text-xl text-[#14512f]">
              {lang === "en" ? "Common questions" : "Sık sorulan sorular"}
            </h3>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div data-testid="faq-trial">
              <div className="text-sm font-semibold text-[#1b2420]">
                {lang === "en" ? "Is there a free trial?" : "Ücretsiz deneme var mı?"}
              </div>
              <p className="mt-1 text-sm text-[#6b7268]">
                {lang === "en"
                  ? "Yes. 3-day full-feature trial. No credit card required — we'll onboard you personally."
                  : "Evet. 3 gün tam özellikli deneme. Kredi kartı gerekmez — kurulumda kişisel destek veriyoruz."}
              </p>
            </div>
            <div data-testid="faq-contract">
              <div className="text-sm font-semibold text-[#1b2420]">
                {lang === "en" ? "Is there a long-term contract?" : "Uzun vadeli sözleşme var mı?"}
              </div>
              <p className="mt-1 text-sm text-[#6b7268]">
                {lang === "en"
                  ? "Monthly by default. Annual billing available on request (2 months free)."
                  : "Varsayılan aylık. Talep üzerine yıllık faturalama (2 ay ücretsiz)."}
              </p>
            </div>
            <div data-testid="faq-cbam">
              <div className="text-sm font-semibold text-[#1b2420]">
                {lang === "en" ? "Does it cover CBAM 2026 requirements?" : "CBAM 2026 gerekliliklerini karşılıyor mu?"}
              </div>
              <p className="mt-1 text-sm text-[#6b7268]">
                {lang === "en"
                  ? "Yes. CBAM quarterly data entry, PDF report and XML export are included from Growth."
                  : "Evet. Growth planından itibaren CBAM çeyrek dönem veri girişi, PDF raporu ve XML export dahil."}
              </p>
            </div>
            <div data-testid="faq-data">
              <div className="text-sm font-semibold text-[#1b2420]">
                {lang === "en" ? "Where is my data stored?" : "Verim nerede saklanıyor?"}
              </div>
              <p className="mt-1 text-sm text-[#6b7268]">
                {lang === "en"
                  ? "EU-hosted infrastructure. Export any time as PDF, XML or JSON — you own your data."
                  : "AB'de barındırılan altyapı. İstediğiniz zaman PDF, XML veya JSON olarak dışa aktarın — veri sizindir."}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col items-center gap-2 border-t border-[#eef0e7] pt-5 text-center">
            <p className="text-sm text-[#4b544c]">
              {lang === "en"
                ? "Have another question? We reply within 24 hours."
                : "Başka bir sorunuz mu var? 24 saat içinde yanıt veriyoruz."}
            </p>
            <button onClick={() => setSelected("starter")} data-testid="faq-contact-btn"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#14512f] px-4 py-2 text-sm font-medium text-white hover:bg-[#0f4526]">
              {lang === "en" ? "Get a quote" : "Teklif Alınız"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <ContactModal open={!!selected} onClose={() => setSelected(null)} plan={selected} />
    </div>
  );
}
